import type { PlaidAccount, PlaidLinkOnExitMetadata } from "react-plaid-link";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import { getPlaidStripeLinkToken, postPlaidStripe } from "~/v2/api/rest/plaid";
import { useScript } from "~/v2/hooks/useScript";

const PLAID_STRIPE_LINK_SCRIPT_SRC =
  "https://cdn.plaid.com/link/v2/stable/link-initialize.js";

interface UsePlaidStripeLinkParams {
  onSuccess: ({
    token,
    plaidAccount,
  }: {
    token: string;
    plaidAccount: PlaidAccount;
  }) => void;
  onError: (errorMessage: string) => void;
  onExit?: (metadata: PlaidLinkOnExitMetadata) => void;
}

export const usePlaidStripeLink = ({
  onSuccess,
  onError,
  onExit,
}: UsePlaidStripeLinkParams) => {
  const scriptStatus = useScript(PLAID_STRIPE_LINK_SCRIPT_SRC, {
    id: "src-plaid",
    removeOnUnmount: true,
  });

  const isReady = scriptStatus === "ready";

  const postPlaidStripeMutation = useMutationRequest(
    (params: { publicToken: string; plaidAccount: PlaidAccount }) =>
      postPlaidStripe({
        publicToken: params.publicToken,
        plaidAccountId: params.plaidAccount.id,
      }),
    {
      onSuccess: (response, params) => {
        const token = response.token;

        if (!token) {
          onError("Error posting Plaid/Stripe: No token found");
          return;
        }

        onSuccess({ token, plaidAccount: params.plaidAccount });
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        onError(`Error posting Plaid/Stripe: ${errorMessage}`);
      },
    },
  );

  const getPlaidStripeLinkTokenMutation = useMutationRequest(
    () => getPlaidStripeLinkToken(),
    {
      onSuccess: (response) => {
        const linkToken = response.link_token;

        if (!linkToken) {
          onError("Error setting up Plaid/Stripe Link: No link token found");
          return;
        }

        const handler = window.Plaid.create({
          token: linkToken,
          onSuccess: async (public_token, metadata) => {
            if (!metadata) {
              onError(
                "Error setting up Plaid/Stripe Link: Plaid metadata is not available",
              );
              return;
            }

            const plaidAccount = metadata.accounts?.at(0);

            if (!plaidAccount) {
              onError(
                "Error setting up Plaid/Stripe Link: No bank account found",
              );
              return;
            }

            postPlaidStripeMutation.mutate({
              publicToken: public_token,
              plaidAccount,
            });
          },
          onExit: (error, metadata) => {
            if (error) {
              console.error("Error setting up Plaid/Stripe Link: ", error);
              onError(
                `Error setting up Plaid/Stripe Link: ${error.display_message}`,
              );
            }

            onExit?.(metadata);
          },
        });

        handler.open();
      },
      onError: (error) => {
        console.error(error);
      },
    },
  );

  const onOpenPlaidStripeLink = async () => {
    // Edge case: disable button if !isReady
    if (!isReady) {
      console.error(
        "Plaid is not ready yet, disable button until isReady is true",
      );
      onError("Plaid is not ready yet, please wait a moment and try again");
      return;
    }

    getPlaidStripeLinkTokenMutation.mutate(true);
  };

  const isLoading =
    postPlaidStripeMutation.isPending ||
    getPlaidStripeLinkTokenMutation.isPending;

  return {
    onOpenPlaidStripeLink,
    isReady,
    isLoading,
    scriptStatus,
  };
};
